import { useState } from 'react'
import { addDays, lastCall, mondayOf, openActions, projectWeeks, todayIso } from '../../../shared/projects.js'
import { CommitInput, CommitText, fmtDate } from './fields.jsx'
import styles from './projects.module.css'

const STATE = {
  held: ['Held', styles.pOk],
  missed: ['Missed', styles.pBad],
  due: ['Not logged yet', styles.pReq],
  upcoming: ['Upcoming', styles.pNone],
}

const body = (call, patch = {}) => ({
  heldOn: call.heldOn,
  attendees: call.attendees,
  summary: call.summary,
  decisions: call.decisions,
  nextOn: call.nextOn,
  actions: call.actions,
  ...patch,
})

/** One record per project per week: a timeline with missed weeks flagged. */
export function CallsTab({ project, api, canEdit }) {
  const today = todayIso()
  const weeks = projectWeeks(project, today)
  const missed = weeks.filter((w) => w.state === 'missed').length
  const open = openActions(project).length
  const [openWeeks, setOpenWeeks] = useState(() => new Set([mondayOf(today)]))

  const toggle = (weekOf, isOpen) =>
    setOpenWeeks((set) => {
      const next = new Set(set)
      if (isOpen) next.add(weekOf)
      else next.delete(weekOf)
      return next
    })

  function logCall(weekOf) {
    // Open actions from the most recent call come along, marked as carried.
    const previous = lastCall(project)
    const carried = (previous?.actions ?? []).filter((a) => !a.done).map((a) => ({ ...a, id: undefined, carried: true }))
    const monday = addDays(weekOf, 2)
    const heldOn = weekOf > mondayOf(today) || monday <= today ? monday : today
    setOpenWeeks((set) => new Set(set).add(weekOf))
    api.saveCall(project.id, weekOf, { heldOn, attendees: '', summary: '', decisions: '', nextOn: addDays(weekOf, 9), actions: carried }, carried.length ? `Call added with ${carried.length} carried action${carried.length > 1 ? 's' : ''}` : 'Call added')
  }

  return (
    <>
      <p className={styles.sub} style={{ margin: 0 }}>
        {project.calls.length} call{project.calls.length === 1 ? '' : 's'} logged · {missed} missed week{missed === 1 ? '' : 's'} · {open} open action{open === 1 ? '' : 's'}
      </p>
      {weeks.map((w) => (
        <details key={w.weekOf} className={styles.week} open={openWeeks.has(w.weekOf)} onToggle={(e) => toggle(w.weekOf, e.currentTarget.open)}>
          <summary>
            <span className={styles.weekName}>
              Week of {fmtDate(w.weekOf)}
              {w.weekOf === mondayOf(today) ? ' (now)' : ''}
            </span>
            <span className={`${styles.pill} ${STATE[w.state][1]}`}>{STATE[w.state][0]}</span>
            <span className={styles.weekSummary}>{w.call?.summary}</span>
            {w.call ? (
              <span className={`${styles.mono} ${styles.muted}`}>
                {fmtDate(w.call.heldOn)}
                {w.call.actions.some((a) => !a.done) ? ` · ${w.call.actions.filter((a) => !a.done).length} open` : ''}
              </span>
            ) : null}
          </summary>
          {w.call ? (
            <CallEditor project={project} call={w.call} api={api} canEdit={canEdit} />
          ) : (
            <div className={styles.weekBody}>
              <p className={styles.muted} style={{ margin: 0 }}>
                {w.state === 'missed' ? 'No call was recorded for this week.' : w.state === 'upcoming' ? 'This call has not happened yet.' : 'No call recorded yet this week.'}
              </p>
              {canEdit ? (
                <div className={styles.row}>
                  <button type="button" className={styles.primary} onClick={() => logCall(w.weekOf)}>
                    {w.state === 'upcoming' ? 'Schedule this call' : 'Log call for this week'}
                  </button>
                  <span className={styles.note}>Open actions from the last call are carried in.</span>
                </div>
              ) : null}
            </div>
          )}
        </details>
      ))}
    </>
  )
}

function CallEditor({ project, call, api, canEdit }) {
  const save = (patch) => api.saveCall(project.id, call.weekOf, body(call, patch))
  const setAction = (id, patch) => save({ actions: call.actions.map((a) => (a.id === id ? { ...a, ...patch } : a)) })
  const field = (id) => `${id}-${call.id}`

  return (
    <div className={styles.weekBody}>
      <div className={styles.form}>
        <label className={styles.fieldLabel}>
          Held on
          <CommitInput type="date" value={call.heldOn} disabled={!canEdit} onCommit={(v) => v && save({ heldOn: v })} />
        </label>
        <label className={styles.fieldLabel}>
          Attendees
          <CommitInput value={call.attendees} disabled={!canEdit} onCommit={(v) => save({ attendees: v })} />
        </label>
        <label className={styles.fieldLabel}>
          Next call
          <CommitInput type="date" value={call.nextOn ?? ''} disabled={!canEdit} onCommit={(v) => save({ nextOn: v || null })} />
        </label>
      </div>
      <label className={styles.fieldLabel}>
        Summary
        <CommitText id={field('s')} value={call.summary} disabled={!canEdit} onCommit={(v) => save({ summary: v })} />
      </label>
      <label className={styles.fieldLabel}>
        Decisions
        <CommitText id={field('d')} value={call.decisions} disabled={!canEdit} style={{ minHeight: 52 }} onCommit={(v) => save({ decisions: v })} />
      </label>
      <div>
        <div className={`${styles.row} ${styles.between}`} style={{ marginBottom: 4 }}>
          <span className={styles.fieldLabel}>Actions</span>
          {canEdit ? (
            <button type="button" className={`${styles.btn} ${styles.sm}`} onClick={() => save({ actions: [...call.actions, { text: '', owner: '', dueOn: null, done: false, carried: false }] })}>
              Add action
            </button>
          ) : null}
        </div>
        {call.actions.length === 0 ? <p className={styles.muted}>No actions from this call.</p> : null}
        {call.actions.map((a) => (
          <div key={a.id} className={`${styles.action} ${a.done ? styles.actionDone : ''}`}>
            <input type="checkbox" checked={a.done} disabled={!canEdit} aria-label="Done" onChange={(e) => setAction(a.id, { done: e.target.checked })} />
            <CommitInput className={`${styles.input} ${styles.actionText}`} type="text" value={a.text} placeholder="What needs to happen" disabled={!canEdit} aria-label="Action" onCommit={(v) => setAction(a.id, { text: v })} />
            {a.carried ? <span className={styles.carried}>carried</span> : null}
            <CommitInput className={`${styles.input} ${styles.actionOwner}`} value={a.owner} placeholder="Owner" disabled={!canEdit} aria-label="Owner" onCommit={(v) => setAction(a.id, { owner: v })} />
            <CommitInput className={`${styles.input} ${styles.actionDue}`} type="date" value={a.dueOn ?? ''} disabled={!canEdit} aria-label="Due" onCommit={(v) => setAction(a.id, { dueOn: v || null })} />
            {canEdit ? (
              <button type="button" className={`${styles.ghost} ${styles.dangerGhost}`} aria-label="Remove action" onClick={() => save({ actions: call.actions.filter((x) => x.id !== a.id) })}>
                Remove
              </button>
            ) : null}
          </div>
        ))}
      </div>
      {canEdit ? (
        <div className={`${styles.row} ${styles.between}`}>
          <span className={styles.note}>Changes save as you leave a field.</span>
          <button type="button" className={`${styles.ghost} ${styles.dangerGhost}`} onClick={() => api.removeCall(project.id, call.weekOf)}>
            Delete this call record
          </button>
        </div>
      ) : null}
    </div>
  )
}
